// Stimulus controllers.
//
// Most load on demand: a controller's module is fetched the first time an
// element on the page names it in data-controller, then registered, and
// Stimulus connects it. Bundling all 76 into application.js cost every page
// ~1s of script evaluation on a mid-range phone for controllers it never uses
// (Lighthouse, 21 Oct 2026). The ones in the layout itself (header menu,
// dropdown, dismissible banners, confirmation modal, analytics) stay eager.
//
// Adding a controller: create it, then add one line to `lazyControllers` (or
// import and register it eagerly if the layout uses it on every page).
import type { ControllerConstructor } from "@hotwired/stimulus"
import { application } from "./application"

import ConfirmationModalController from "./confirmation_modal_controller"
import NavMenuController from "./nav_menu_controller"
import DismissibleController from "./dismissible_controller"
import DropdownController from "./dropdown_controller"
import AnalyticsController from "./analytics_controller"

application.register("confirmation-modal", ConfirmationModalController)
application.register("nav-menu", NavMenuController)
application.register("dismissible", DismissibleController)
application.register("dropdown", DropdownController)
application.register("analytics", AnalyticsController)

type ControllerModule = { default: ControllerConstructor }

const lazyControllers: Record<string, () => Promise<ControllerModule>> = {
  "hello": () => import("./hello_controller"),
  "carousel": () => import("./carousel_controller"),
  "business-selector": () => import("./business_selector_controller"),
  "multi-step-form": () => import("./multi_step_form_controller"),
  "earnings-calculator": () => import("./earnings_calculator_controller"),
  "cost-estimator": () => import("./cost_estimator_controller"),
  "licence-cost": () => import("./licence_cost_controller"),
  "accordion": () => import("./accordion_controller"),
  "vehicle-selector": () => import("./vehicle_selector_controller"),
  "password-visibility": () => import("./password_visibility_controller"),
  "suburb-picker": () => import("./suburb_picker_controller"),
  "sidebar-nav": () => import("./sidebar_nav_controller"),
  "service-area-map": () => import("./service_area_map_controller"),
  "read-more": () => import("./read_more_controller"),
  "toggle": () => import("./toggle_controller"),
  "pass-rate-toggle": () => import("./pass_rate_toggle_controller"),
  "vehicle-image-preview": () => import("./vehicle_image_preview_controller"),
  "drive-score-chart": () => import("./drive_score_chart_controller"),
  "suburb-autocomplete": () => import("./suburb_autocomplete_controller"),
  "instructor-filter": () => import("./instructor_filter_controller"),
  "test-centre-select": () => import("./test_centre_select_controller"),
  "user-signup": () => import("./user_signup_controller"),
  "admin-pass-rate": () => import("./admin_pass_rate_controller"),
  "admin-verification-form": () => import("./admin_verification_form_controller"),
  "file-upload": () => import("./file_upload_controller"),
  "bio-word-count": () => import("./bio_word_count_controller"),
  "edit-toggle": () => import("./edit_toggle_controller"),
  "tom-select": () => import("./tom_select_controller"),
  "student-vehicle-toggle": () => import("./student_vehicle_toggle_controller"),
  "modal": () => import("./modal_controller"),
  "documents": () => import("./documents_controller"),
  "direct-upload": () => import("./direct_upload_controller"),
  "char-counter": () => import("./char_counter_controller"),
  "document-update": () => import("./document_update_controller"),
  "document-card": () => import("./document_card_controller"),
  "logbook-calculator": () => import("./logbook_calculator_controller"),
  "scroll-region": () => import("./scroll_region_controller"),
  "suburb-map": () => import("./suburb_map_controller"),
  "licence-path-tool": () => import("./licence_path_tool_controller"),
  "licence-conversion-checker": () => import("./licence_conversion_checker_controller"),
  "test-centre-quiz": () => import("./test_centre_quiz_controller"),
  "practice-test": () => import("./practice_test_controller"),
  "hazard-perception": () => import("./hazard_perception_controller"),
  "opening-hours": () => import("./opening_hours_controller"),
  "calendar-event": () => import("./calendar_event_controller"),
  "date-picker": () => import("./date_picker_controller"),
  "time-slots": () => import("./time_slots_controller"),
  "booking-wizard": () => import("./booking_wizard_controller"),
  "checkout-credits": () => import("./checkout_credits_controller"),
  "stripe-checkout": () => import("./stripe_checkout_controller"),
  "star-rating": () => import("./star_rating_controller"),
  "clipboard": () => import("./clipboard_controller"),
  "availability-modal": () => import("./availability_modal_controller"),
  "booking-for-toggle": () => import("./booking_for_toggle_controller"),
  "address-autocomplete": () => import("./address_autocomplete_controller"),
  "hours-select": () => import("./hours_select_controller"),
  "order-summary": () => import("./order_summary_controller"),
  "schedule-package": () => import("./schedule_package_controller"),
  "add-booking": () => import("./add_booking_controller"),
  "duration-selector": () => import("./duration_selector_controller"),
  "schedule-modal": () => import("./schedule_modal_controller"),
  "phone-input": () => import("./phone_input_controller"),
  "email-input": () => import("./email_input_controller"),
  "image-fallback": () => import("./image_fallback_controller"),
  "onboarding-section": () => import("./onboarding_section_controller"),
  "day-popup": () => import("./day_popup_controller"),
  "scroll-into-view": () => import("./scroll_into_view_controller"),
  "comparison-swipe": () => import("./comparison_swipe_controller"),
  "sticky-cta": () => import("./sticky_cta_controller"),
  "submit-disable": () => import("./submit_disable_controller"),
  "test-date-form": () => import("./test_date_form_controller"),
}

const requested = new Set<string>()

function load(identifier: string): void {
  const importer = lazyControllers[identifier]
  if (!importer || requested.has(identifier)) return
  requested.add(identifier)
  importer()
    .then((module) => application.register(identifier, module.default))
    .catch((error) => {
      requested.delete(identifier)
      console.error(`Failed to load the ${identifier} controller`, error)
    })
}

function loadFor(element: Element): void {
  const names = element.getAttribute("data-controller")
  if (names) names.split(/\s+/).forEach((name) => name && load(name))
}

function loadWithin(root: ParentNode): void {
  if (root instanceof Element) loadFor(root)
  root.querySelectorAll("[data-controller]").forEach(loadFor)
}

loadWithin(document)

// Turbo swaps the body on every visit, and some views add controllers later
// (modals, frames), so watch for new elements and changed data-controller.
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === "attributes") {
      loadFor(mutation.target as Element)
    } else {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) loadWithin(node)
      })
    }
  }
}).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ["data-controller"],
})
